import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Api from '../../../../../services/Api';
import BasePage from '../../../BasePage/BasePage';
import ImageCarousel from '../../../../atoms/ImageCarousel/ImageCarousel';
import Grid from '../../../../atoms/Grid/Grid';
import InputControl from '../../../../atoms/InputControl/InputControl';
import TagsInput from '../../../../atoms/TagsInput/TagsInput';
import DynastySelector from '../../../../atoms/DynastySelector/DynastySelector';
import CitySelector from '../../../../atoms/CitySelector/CItySelector';
import CitationEditor from '../../../../molecules/CitationEditor/CitationEditor';
import Button from '../../../../atoms/Button/Button';
import Icon from '../../../../atoms/Icon/Icon';
import {
  fetchContextsInitAction,
  getContextSuccessAction,
  updateSelectedContextAction,
} from '../../../../../actions/instances/context';
import BuildingsSelector from '../../../../atoms/BuildingsSelector/BuildingsSelector';
import extractErrors from '../../../../../helpers/errors';
import ContextTypeSelector from '../../../../atoms/ContextsTypeSelector/ContextTypeSelector';
import InstanceDocumentEditor from '../../../../Organisms/InstanceDocumentEditor/InstanceDocumentEditor';
import './CreateContext.scss';

const CreateContext = () => {
  const { selectedContext } = useSelector(state => state.contexts);
  const [context, setContext] = useState(selectedContext);
  useEffect(() => {
    if (selectedContext) {
      Api.get(`instances/building-contexts/${selectedContext.id}`).then(response => setContext(response.data));
    }
  }, []);
  let mainImage;
  let restOfImages = [];
  if (context && context.images) {
    if (context.images.length > 0) {
      [mainImage] = context.images;
      restOfImages = context.images.slice(0, 1);
    }
  }
  const [imagesIds, setImagesIds] = useState(restOfImages);
  const [mainImageId, setMainImageId] = useState(mainImage);
  const [name, setName] = useState(context ? context.name : undefined);
  const [scholar, setScholar] = useState(context ? context.scholar : undefined);
  const [invention, setInvention] = useState(context ? context.invention : undefined);
  const [startDate, setStartDate] = useState(context ? context.start_date : undefined);
  const [endDate, setEndDate] = useState(context ? context.end_date : undefined);
  const [contextTypeId, setContextTypeId] = useState(context ? context.context_type_id : undefined);
  const [dynastyCode, setDynastyCode] = useState(context ? context.dynasty.code : undefined);
  const [dynastyId, setDynastyId] = useState(context ? context.dynasty_id : undefined);
  const [cityId, setCityId] = useState(context ? context.city.id : undefined);
  const [cityCode, setCityCode] = useState(context ? context.city.code : undefined);
  const [resourcesIds, setResourcesIds] = useState(
    context && context.citation ? context.citation.map(c => c.id) : undefined,
  );
  const [documentsIds, setDocumentsIds] = useState(
    context ? context.documents.map(doc => doc.id) : [],
  );
  const [errors, setErrors] = useState(undefined);
  const [tagsIds, setTagsIds] = useState(
    context && context.tags ? context.tags.map(t => t.id) : undefined,
  );
  const [buildingsIds, setBuildingsIds] = useState(
    context && context.buildings ? context.buildings.map(x => x.id) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [identifier, setIdentifier] = useState(
    context ? `${context.city.code}-${context.dynasty.code}-${context.code}` : undefined,
  );
  useEffect(() => {
    let combinedCode = '';
    if (cityCode) {
      combinedCode += `${cityCode}-`;
    }
    if (dynastyCode) {
      combinedCode += `${dynastyCode}-`;
    }
    combinedCode += 'SCNXXX';
    setIdentifier(combinedCode);
  }, [cityCode, dynastyCode]);
  useEffect(() => {
    if (context && context.buildings) {
      setBuildingsIds(context.buildings.map(x => x.id));
    }
  }, [context]);
  const dispatch = useDispatch();
  const history = useHistory();
  const handleSave = () => {
    if (!name) {
      setErrors('Context name is required');
      return;
    }
    if (!contextTypeId) {
      setErrors('You must select a type for the context');
      return;
    }
    if (!cityId) {
      setErrors('You must select a region for the context');
      return;
    }
    if (!dynastyId) {
      setErrors('You must select a dynasty for the context');
      return;
    }
    setIsLoading(true);
    const data = {
      name,
      scholar,
      invention,
      code_suffix: 'SCN',
      city_id: cityId,
      dynasty_id: dynastyId,
      images_ids: [mainImageId, ...imagesIds].filter(img => img !== undefined),
      context_type_id: contextTypeId,
      tags_ids: tagsIds,
      buildings_ids: buildingsIds,
      resources_ids: resourcesIds,
      documents_ids: documentsIds,
      start_date: startDate,
      end_date: endDate,
    };
    const callback = (response) => {
      if (response.status === 200 || response.status === 201) {
        dispatch(fetchContextsInitAction());
        dispatch(updateSelectedContextAction(response.data));
        setTimeout(() => {
          setIsLoading(false);
          history.goBack();
        }, 250);
      } else {
        const errs = extractErrors(response.data);
        setErrors([errs[0]]);
        setIsLoading(false);
      }
    };
    if (context) {
      Api.patch(`instances/building-contexts/${context.id}`, data).then(response => callback(response));
    } else {
      Api.post('instances/building-contexts', data).then(response => callback(response));
    }
  };
  return (
    <BasePage>
      <div className="create-component-container">
        <h2 className="col-md-12">
          {context ? `Edit ${context.name}` : 'Create new context'}
        </h2>
        <br />
        <Grid>
          <div className="col-md-3 left-column">
            <ImageCarousel
              initialImages={context ? context.images : []}
              additionalImagesAddedCallback={images => setImagesIds(images)}
              mainImageAddedCallback={imageId => setMainImageId(imageId)}
            />
            <div className="col-md-12 dynasty-section mt-2">
              <InstanceDocumentEditor
                initialDocuments={context ? context.documents : []}
                onFilesChanged={(files) => {
                  setDocumentsIds(files.map(f => f.id));
                }}
              />
            </div>
            <div className="tags-container">
              <label className="tag-label">Context Tags</label>
              <TagsInput tags={tagsIds} placeholder="Start typing..." onChange={tags => setTagsIds(tags)} />
            </div>
          </div>
          <div className="col-md-9 content-column">
            <Grid>
              <div className="col-md-12 dynasty-section">
                <label>Context Type</label>
                <ContextTypeSelector
                  selectedId={contextTypeId}
                  onChanged={(type) => {
                    setContextTypeId(type.value);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Context Location</label>
                <CitySelector
                  selectedId={cityId}
                  onChanged={(city) => {
                    setCityId(city.value);
                    setCityCode(city.code);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Context Dynasty</label>
                <DynastySelector
                  selectedId={dynastyId}
                  onChanged={(dynasty) => {
                    setDynastyId(dynasty.value);
                    setDynastyCode(dynasty.code);
                  }}
                />
              </div>
              <div className="col-md-12">
                <InputControl label="Scholar" value={scholar} type="text" onValueChanged={value => setScholar(value)} placeholder="Scholar..." />
              </div>
              <div className="col-md-12">
                <InputControl label="Invention" value={invention} type="text" onValueChanged={value => setInvention(value)} placeholder="Invention..." />
              </div>
              <div className="col-md-6 mt-3">
                <InputControl label="Start Date" value={startDate} type="number" onValueChanged={value => setStartDate(value)} placeholder="Start Date..." />
              </div>
              <div className="col-md-6 mt-3">
                <InputControl label="End Date" value={endDate} type="number" onValueChanged={value => setEndDate(value)} placeholder="End Date..." />
              </div>
              <div className="col-md-12">
                <InputControl label="Title" value={name} type="text" onValueChanged={value => setName(value)} placeholder="Title..." />
                <small>{identifier}</small>
              </div>
              <div className="col-md-12 dynasty-section">
                <label>Context Buildings</label>
                <BuildingsSelector
                  cityId={cityId}
                  dynastyId={dynastyId}
                  selectedIds={buildingsIds}
                  onChanged={(values) => {
                    const ids = values.map(val => parseInt(val.value, 10));
                    setBuildingsIds(ids);
                  }}
                />
              </div>
              <div className="col-md-12 dynasty-section">
                <CitationEditor
                  onChange={values => setResourcesIds(values)}
                  initialCitations={context ? context.citation : []}
                />
              </div>
              {errors && (
                <div className="col-md-12 errors-container">
                  <span className="error">{errors}</span>
                </div>
              )}
              <div className="col-md-12 actions">
                <Button
                  onInvoked={() => {
                    dispatch(getContextSuccessAction(undefined));
                    history.goBack();
                  }}
                >
                  <Icon icon="times" />
                  <span>Cancel</span>
                </Button>
                <Button onInvoked={handleSave} isLoading={isLoading}>
                  <Icon icon="save" />
                  <span>Save</span>
                </Button>
              </div>
            </Grid>
          </div>
        </Grid>
      </div>
    </BasePage>
  );
};

export default CreateContext;
